import React, { useEffect, useId, useRef, useState } from 'react'
import { useMantineTheme } from '@mantine/core'

const TOOLTIP_MAX_WIDTH = 240
const TOOLTIP_MIN_WIDTH = 180

const PurchasedItemCard = ({
	name,
	imageUrl,
	description,
	issued,
	disabled,
	onToggle,
}) => {
	const theme = useMantineTheme()
	const isDark = theme.colorScheme === 'dark'
	const controlBorderColor = isDark ? theme.colors.dark[2] : '#789'
	const handleClick = () => {
		if (!disabled) onToggle()
	}
	const hasDescription =
		typeof description === 'string' && description.trim().length > 0
	const tooltipText = hasDescription ? description.trim() : ''
	const [isInfoHovered, setIsInfoHovered] = useState(false)
	const [isInfoPinned, setIsInfoPinned] = useState(false)
	const infoContainerRef = useRef(null)
	const tooltipId = useId()
	const showTooltip = hasDescription && (isInfoHovered || isInfoPinned)

	useEffect(() => {
		if (!isInfoPinned) return undefined

		const handleOutsidePress = (event) => {
			if (!infoContainerRef.current?.contains(event.target)) {
				setIsInfoPinned(false)
			}
		}

		document.addEventListener('mousedown', handleOutsidePress)
		document.addEventListener('touchstart', handleOutsidePress)

		return () => {
			document.removeEventListener('mousedown', handleOutsidePress)
			document.removeEventListener('touchstart', handleOutsidePress)
		}
	}, [isInfoPinned])

	return (
		<div
			onClick={handleClick}
			style={{
				cursor: disabled ? 'default' : 'pointer',
				border: issued ? '3px solid #4caf50' : '3px solid transparent',
				borderRadius: '8px',
				padding: '1rem',
				textAlign: 'center',
				backgroundColor: isDark ? theme.colors.dark[6] : '#f9f9f9',
				color: isDark ? theme.colors.dark[0] : theme.black,
				boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
				opacity: disabled ? 0.5 : 1,
				transition: 'all 0.2s ease-in-out',
				position: 'relative',
			}}
		>
			{hasDescription ? (
				<div
					ref={infoContainerRef}
					onClick={(e) => e.stopPropagation()}
					onMouseEnter={() => setIsInfoHovered(true)}
					onMouseLeave={() => setIsInfoHovered(false)}
					style={{
						position: 'absolute',
						top: '10px',
						left: '10px',
						zIndex: 5,
					}}
				>
					<button
						type='button'
						aria-label='Item description'
						aria-describedby={showTooltip ? tooltipId : undefined}
						aria-expanded={showTooltip}
						onFocus={() => setIsInfoHovered(true)}
						onBlur={() => setIsInfoHovered(false)}
						onKeyDown={(event) => {
							if (event.key === 'Escape') {
								setIsInfoPinned(false)
								setIsInfoHovered(false)
							}
						}}
						onClick={() => setIsInfoPinned((prev) => !prev)}
						style={{
							width: '24px',
							height: '24px',
							borderRadius: '50%',
							border: `2px solid ${controlBorderColor}`,
							backgroundColor: isDark ? theme.colors.dark[5] : '#fff',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							fontSize: '14px',
							fontWeight: 700,
							color: isDark ? theme.colors.dark[0] : '#456',
							cursor: 'help',
							userSelect: 'none',
							padding: 0,
						}}
					>
						i
					</button>
					{showTooltip ? (
						<div
							id={tooltipId}
							role='tooltip'
							style={{
								position: 'absolute',
								top: '30px',
								left: 0,
								width: `clamp(${TOOLTIP_MIN_WIDTH}px, 60vw, ${TOOLTIP_MAX_WIDTH}px)`,
								maxWidth: 'calc(100vw - 32px)',
								textAlign: 'left',
								whiteSpace: 'pre-line',
								overflowWrap: 'break-word',
								backgroundColor: '#222',
								color: '#fff',
								borderRadius: '6px',
								padding: '6px 8px',
								fontSize: '12px',
								lineHeight: 1.3,
								boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
							}}
						>
							{tooltipText}
						</div>
					) : null}
				</div>
			) : null}

			<h5 style={{ marginBottom: '0.75rem' }}>{name}</h5>

			{imageUrl ? (
				<img
					src={imageUrl}
					alt={name}
					style={{
						width: '100%',
						maxHeight: '100px',
						objectFit: 'contain',
						borderRadius: '6px',
						marginBottom: '0.5rem',
					}}
				/>
			) : (
				<div
					style={{
						height: '100px',
						backgroundColor: isDark ? theme.colors.dark[4] : '#ddd',
						borderRadius: '6px',
						marginBottom: '0.5rem',
					}}
				/>
			)}

			<div
				style={{
					position: 'absolute',
					top: '10px',
					right: '10px',
					width: '24px',
					height: '24px',
					borderRadius: '50%',
					border: `2px solid ${controlBorderColor}`,
					backgroundColor: issued ? '#4caf50' : 'transparent',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					fontSize: '16px',
					color: '#fff',
				}}
			>
				{issued ? '✓' : ''}
			</div>
		</div>
	)
}

export default PurchasedItemCard
